<template>
  <!-- Add Resignation -->
  <div
    class="modal fade dq-resignation-modal"
    id="new_resignation"
    tabindex="-1"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-door-exit"></i>
            </div>

            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add Resignation</h4>
              <div class="dq-subtitle">
                Record an employee resignation with notice date, reason and
                effective date.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-modal-body">
            <!-- Tips -->
            <div class="dq-info-row">
              <div class="dq-info-pill">
                <i class="ti ti-bulb"></i>
                <span
                  >Tip: Set notice date first, then resignation date for clean
                  records.</span
                >
              </div>
            </div>

            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-clipboard-text"></i> Resignation Details
                </div>
                <div class="dq-card-sub">
                  Choose an employee, add reason, confirm dates, and select
                  status.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <!-- Employee (searchable dropdown) -->
                <div class="col-12">
                  <label class="form-label dq-label">
                    Resigning Employee <span class="text-danger">*</span>
                  </label>

                  <div
                    class="dropdown w-100"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                      @click="seedEmployeeDropdown()"
                    >
                      <span class="text-truncate">
                        {{ selectedEmployeeLabel || "Select employee" }}
                      </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div class="dropdown-menu p-2 w-100 shadow dq-dd-menu">
                      <input
                        v-model="empQuery"
                        type="text"
                        class="form-control dq-input mb-2"
                        placeholder="Search employee…"
                        autocomplete="off"
                      />

                      <div class="dq-search-list">
                        <div
                          v-if="empLoading"
                          class="text-center py-2 small text-muted"
                        >
                          Loading...
                        </div>

                        <button
                          v-for="opt in filteredEmployees"
                          :key="'emp-r-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          @click="chooseEmployee(opt)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-user"></i>
                            </div>
                            <div class="dq-dd-text">
                              <div class="dq-dd-title text-truncate">
                                {{ opt.label }}
                              </div>
                              <div class="dq-dd-sub text-truncate">
                                {{ opt.meta || "Employee" }}
                              </div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!empLoading && filteredEmployees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No employees found
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">
                    Required. Search by name or employee number.
                  </div>
                </div>

                <!-- Notice Date -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Notice Date</label>
                  <date-picker
                    v-model="form.notice_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                  <div class="dq-help">
                    Optional. Date resignation notice was submitted.
                  </div>
                </div>

                <!-- Resignation Date -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Resignation Date</label>
                  <date-picker
                    v-model="form.resignation_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                  <div class="dq-help">Effective resignation date.</div>
                </div>

                <!-- Reason -->
                <div class="col-12">
                  <label class="form-label dq-label">Reason</label>
                  <textarea
                    v-model="form.reason"
                    class="form-control dq-input"
                    rows="4"
                    placeholder="Add reason or notes..."
                  ></textarea>
                </div>

                <!-- Status -->
                <div class="col-12">
                  <label class="form-label dq-label">Status</label>

                  <vue-select
                    v-model="form.status"
                    :options="['Draft', 'Approved', 'Rejected']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />

                  <div
                    class="dq-chip mt-2"
                    :class="statusChipClass(form.status)"
                  >
                    <i class="ti ti-progress"></i>
                    <span>{{ statusChipText(form.status) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :class="{ disabled: addingResignation }"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary">
              <span
                v-if="addingResignation"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Add Resignation <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Resignation -->
  <div
    class="modal fade dq-resignation-modal"
    id="edit_resignation"
    tabindex="-1"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>

            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Edit Resignation</h4>
              <div class="dq-subtitle">
                Update resignation details. Changes are tracked for audit.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-settings"></i> Update Details
                </div>
                <div class="dq-card-sub">
                  Verify dates and status before saving.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <!-- Employee searchable dropdown -->
                <div class="col-12">
                  <label class="form-label dq-label">
                    Resigning Employee <span class="text-danger">*</span>
                  </label>

                  <div
                    class="dropdown w-100"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                      @click="seedEmployeeDropdown()"
                    >
                      <span class="text-truncate">
                        {{ selectedEmployeeLabel || "Select employee" }}
                      </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div class="dropdown-menu p-2 w-100 shadow dq-dd-menu">
                      <input
                        v-model="empQuery"
                        type="text"
                        class="form-control dq-input mb-2"
                        placeholder="Search employee…"
                        autocomplete="off"
                      />

                      <div class="dq-search-list">
                        <div
                          v-if="empLoading"
                          class="text-center py-2 small text-muted"
                        >
                          Loading...
                        </div>

                        <button
                          v-for="opt in filteredEmployees"
                          :key="'emp-re-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          @click="chooseEmployee(opt)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-user"></i>
                            </div>
                            <div class="dq-dd-text">
                              <div class="dq-dd-title text-truncate">
                                {{ opt.label }}
                              </div>
                              <div class="dq-dd-sub text-truncate">
                                {{ opt.meta || "Employee" }}
                              </div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!empLoading && filteredEmployees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No employees found
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">Search by name or employee number.</div>
                </div>

                <!-- Notice Date -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Notice Date</label>
                  <date-picker
                    v-model="form.notice_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <!-- Resignation Date -->
                <div class="col-md-6">
                  <label class="form-label dq-label">Resignation Date</label>
                  <date-picker
                    v-model="form.resignation_date"
                    class="form-control datetimepicker dq-input"
                    placeholder="dd/mm/yyyy"
                    :editable="true"
                    :clearable="false"
                    :input-format="dateFormat"
                  />
                </div>

                <!-- Reason -->
                <div class="col-12">
                  <label class="form-label dq-label">Reason</label>
                  <textarea
                    v-model="form.reason"
                    class="form-control dq-input"
                    rows="4"
                    placeholder="Add notes..."
                  ></textarea>
                </div>

                <!-- Status -->
                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                  <select v-model="form.status" class="form-select dq-input">
                    <option>Draft</option>
                    <option>Approved</option>
                    <option>Rejected</option>
                  </select>

                  <div
                    class="dq-chip mt-2"
                    :class="statusChipClass(form.status)"
                  >
                    <i class="ti ti-progress"></i>
                    <span>{{ statusChipText(form.status) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-light dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="!isEdit"
            >
              <span
                v-if="addingResignation"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Save Changes <i class="ti ti-device-floppy ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-resignation-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div class="dq-headtext">
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action can’t be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this resignation record?</div>
              <div class="dq-danger-sub">
                Are you sure you want to delete this resignation record?
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <a
            href="javascript:void(0);"
            class="btn btn-light dq-btn dq-btn-muted me-2"
            data-bs-dismiss="modal"
          >
            Cancel
          </a>

          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useResignationStore } from "@/stores/resignationStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const resigStore = useResignationStore();
const empStore = useEmployeeStore();

const deleting = ref(false);
const addingResignation = computed(() => resigStore.loading);
const dateFormat = "dd/MM/yyyy";

// force-refresh keys (helps reset stubborn inputs between opens)
const addKey = ref(0);
const editKey = ref(0);

const form = ref(blankForm());

function blankForm() {
  return {
    employee_id: "",
    department_id: "",
    notice_date: null, // keep Date in UI; convert before submit
    reason: "",
    resignation_date: null, // keep Date in UI; convert before submit
    status: "Draft",
  };
}

// mutate in-place so v-model inputs update reliably
function resetForm() {
  const blank = blankForm();
  for (const k in blank) form.value[k] = blank[k];
}

// preload employees if needed
(async () => {
  if (!empStore.employees?.length) await empStore.fetchEmployees();
})();

// keep the form in sync with editItem
watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.employee_id = val.employee_id ?? "";
      form.value.department_id = val.department_id ?? "";
      form.value.notice_date = val.notice_date
        ? new Date(val.notice_date)
        : null;
      form.value.reason = val.reason ?? "";
      form.value.resignation_date = val.resignation_date
        ? new Date(val.resignation_date)
        : null;
      form.value.status = val.status ?? "Draft";
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

// --- DATE UTILS: send YYYY-MM-DD to avoid UTC shifting on backend
function toYMD(d) {
  if (!d) return null;
  if (typeof d === "string") return d;
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

async function submitCreate() {
  if (!form.value.employee_id) {
    toast("Employee is required", "error");
    return;
  }
  const payload = {
    ...form.value,
    notice_date: toYMD(form.value.notice_date),
    resignation_date: toYMD(form.value.resignation_date),
  };
  await resigStore.create(payload);
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  const payload = {
    ...form.value,
    notice_date: toYMD(form.value.notice_date),
    resignation_date: toYMD(form.value.resignation_date),
  };
  await resigStore.update(props.editItem.id, payload);
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document.querySelector('#new_resignation [data-bs-dismiss="modal"]')?.click();
}
function closeEdit() {
  document
    .querySelector('#edit_resignation [data-bs-dismiss="modal"]')
    ?.click();
}

const employeesForSelect = computed(() =>
  (empStore.employees || []).map((e) => ({
    id: e.id,
    label: `${e?.user?.first_name ?? e.first_name ?? ""} ${
      e?.user?.last_name ?? e.last_name ?? ""
    } (${e.employee_id})`.trim(),
    department_id: e.department_id,
    meta: e?.department?.name || e?.designation?.name || "Employee",
  }))
);

/* ===================== Employee searchable dropdown UI ===================== */
const empQuery = ref("");
const empLoading = ref(false);

const selectedEmployeeLabel = computed(() => {
  const id = Number(form.value.employee_id || 0);
  const found = employeesForSelect.value.find((x) => Number(x.id) === id);
  return found?.label || "";
});

const filteredEmployees = computed(() => {
  const q = empQuery.value.trim().toLowerCase();
  if (!q) return employeesForSelect.value.slice(0, 8);
  return employeesForSelect.value
    .filter((e) => (e.label || "").toLowerCase().includes(q))
    .slice(0, 30);
});

function seedEmployeeDropdown() {
  empQuery.value = "";
}
function chooseEmployee(opt) {
  form.value.employee_id = opt.id;
  form.value.department_id = opt.department_id ?? form.value.department_id;
}

/* ===================== Status chip helpers ===================== */
function statusChipClass(v) {
  const x = String(v || "").toLowerCase();
  if (x === "approved") return "dq-chip-ok";
  if (x === "rejected") return "dq-chip-danger";
  if (x === "draft") return "dq-chip-warn";
  return "dq-chip-muted";
}
function statusChipText(v) {
  if (!v) return "Status";
  return `Status: ${v}`;
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await resigStore.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

// ensure state clears whenever modals hide/show
let cleanup = null;
onMounted(() => {
  const addEl = document.getElementById("new_resignation");
  const editEl = document.getElementById("edit_resignation");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
.dq-dialog {
  max-width: 820px;
}

/* Shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}
.dq-modal-body {
  padding: 16px 20px;
}

/* Reliable scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 150px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.14);
  color: rgb(79, 70, 229);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Tips */
.dq-info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.dq-info-pill {
  flex: 1 1 260px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.72);
  font-weight: 800;
  font-size: 0.82rem;
}
.dq-info-pill i {
  color: rgba(99, 102, 241, 0.85);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Dropdown */
.dq-dd-btn {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-dd-menu {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}
.dq-search-list {
  max-height: 280px;
  overflow: auto;
  overscroll-behavior: contain;
}
.dq-dd-item {
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-dd-item:hover {
  background: rgba(15, 23, 42, 0.04);
}
.dq-dd-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.dq-avatar {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.1);
  color: rgb(79, 70, 229);
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-dd-title {
  font-weight: 900;
}
.dq-dd-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted {
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.7);
}
.dq-chip-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}
.dq-chip-warn {
  background: rgba(245, 158, 11, 0.12);
  color: rgb(217, 119, 6);
}
.dq-chip-danger {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

@media (max-width: 576px) {
  .dq-dialog {
    max-width: 96vw;
    margin: 0.75rem auto;
  }
}
</style>
